<template>
  <div v-if="isActive || isPaused">
    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn
          variant="outlined"
          size="small"
          v-bind="props"
          :disabled="isLoading"
        >
          {{ $t('lead.base-info.channel.status.label') }}
          <v-icon end> mdi-chevron-down </v-icon>
        </v-btn>
      </template>

      <v-list density="compact">
        <template v-if="isActive">
          <v-list-item
            v-if="showsChannelPauseStopControls"
            @click="pause"
          >
            <template #prepend>
              <v-icon color="warning"> mdi-pause-circle-outline </v-icon>
            </template>
            <v-list-item-title>{{ $t('lead.base-info.channel.status.pause') }}</v-list-item-title>
          </v-list-item>

          <v-list-item @click="confirmStop = true">
            <template #prepend>
              <v-icon color="error"> mdi-stop-circle-outline </v-icon>
            </template>
            <v-list-item-title class="text-error">
              {{ $t('lead.base-info.channel.status.stop') }}
            </v-list-item-title>
          </v-list-item>
        </template>

        <template v-if="isPaused">
          <v-list-item
            v-if="showsChannelPauseStopControls"
            @click="resume"
          >
            <template #prepend>
              <v-icon color="success"> mdi-play-circle-outline </v-icon>
            </template>
            <v-list-item-title>{{ $t('lead.base-info.channel.status.resume') }}</v-list-item-title>
          </v-list-item>

          <v-list-item @click="confirmStop = true">
            <template #prepend>
              <v-icon color="error"> mdi-stop-circle-outline </v-icon>
            </template>
            <v-list-item-title class="text-error">
              {{ $t('lead.base-info.channel.status.stop') }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <v-dialog
      v-model="confirmStop"
      width="550"
    >
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar
            color="error"
            dark
            flat
          >
            <v-toolbar-title>{{ $t('lead.base-info.channel.stop') }}</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                @click="confirmStop = false"
              >
                <v-icon size="large"> mdi-close </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="mt-4">
          {{ $t('lead.base-info.channel.stop-confirm') }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            v-if="showsChannelPauseStopControls"
            variant="text"
            :disabled="isLoading"
            @click="pauseAndClose"
          >
            {{ $t('lead.base-info.channel.stop-no-pause') }}
          </v-btn>

          <v-btn
            color="error"
            variant="text"
            :disabled="isLoading"
            @click="stop"
          >
            {{ $t('lead.base-info.channel.stop-yes-deactivate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import bus, { eventNames } from '@/lib/eventBus'
import crmMixin from '@/mixins/crm.js'
import featureMixin from '@/mixins/feature'
import NEXT_MESSAGE from '@/modules/leads/Lead/queries/NextMessage.gql'

import LEAD from './queries/Lead.gql'
import PAUSE_CHANNEL_FOR_LEAD from './queries/PauseChannelForLead.gql'
import RESUME_CHANNEL_FOR_LEAD from './queries/ResumeChannelForLead.gql'
import STOP_ALL_COMMUNICATIONS_FOR_LEAD from './queries/StopAllCommunicationsForLead.gql'

export default {
  mixins: [featureMixin, crmMixin],
  props: {
    leadId: { type: String, required: true },
    channel: { type: Object, required: true },
  },
  data() {
    return {
      confirmStop: false,
      isLoading: false,
    }
  },
  computed: {
    showsChannelPauseStopControls() {
      return this.isFeatureActive(this.featureNames.LEAD_CHANNEL_PAUSE_STOP)
    },
    isActive() {
      return !!this.channel.subscribedAt && !this.channel.unsubscribedAt && !this.channel.pausedAt
    },
    isPaused() {
      return !!this.channel.pausedAt && !this.channel.unsubscribedAt
    },
  },
  methods: {
    async mutateChannel(mutation, successAlert, errorAlert) {
      this.isLoading = true
      try {
        await this.$apollo.mutate({
          mutation,
          variables: {
            input: {
              leadId: this.leadId,
              channelId: this.channel.channelId,
            },
          },
          refetchQueries: [
            { query: LEAD, variables: { id: this.leadId } },
            { query: NEXT_MESSAGE, variables: { contactId: this.$route?.params?.id } },
          ],
        })
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t(successAlert),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t(errorAlert),
        })
      } finally {
        this.isLoading = false
      }
    },
    async stop() {
      this.isLoading = true
      try {
        await this.$apollo.mutate({
          mutation: STOP_ALL_COMMUNICATIONS_FOR_LEAD,
          variables: {
            input: {
              leadId: this.leadId,
            },
          },
          refetchQueries: [
            { query: LEAD, variables: { id: this.leadId } },
            { query: NEXT_MESSAGE, variables: { contactId: this.$route?.params?.id } },
          ],
        })
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.lead.base-info.channel.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.lead.base-info.channel.error'),
        })
      } finally {
        this.isLoading = false
        this.confirmStop = false
      }
    },
    async pauseAndClose() {
      if (this.isActive) {
        await this.pause()
      }
      this.confirmStop = false
    },
    async pause() {
      await this.mutateChannel(
        PAUSE_CHANNEL_FOR_LEAD,
        'alerts.lead.base-info.channel.success-pause',
        'alerts.lead.base-info.channel.error-pause'
      )
    },
    async resume() {
      await this.mutateChannel(
        RESUME_CHANNEL_FOR_LEAD,
        'alerts.lead.base-info.channel.success-resume',
        'alerts.lead.base-info.channel.error-resume'
      )
    },
  },
}
</script>
